import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ResolveFn, Router } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageKey, StorageService } from '../../core/services/storage.service';

/**
 * Minimal kitchen identity returned by the KDS launch endpoint. The KDS
 * shell uses this to populate the kitchen filter dropdown without a
 * second round-trip to `/api/stores/{id}/kitchens` (which is gated by
 * StoreManage permission and inaccessible to a kdsToken).
 */
export interface KdsKitchenSummary {
  id: string;
  name: string;
  displayOrder: number;
}

/**
 * Full launch response from POST /api/public/store/{storeCode}/kds-launch.
 * Stored verbatim in `StorageKey.KDS_LAUNCH` so the KDS shell can read
 * storeId / storeName / kitchens directly without depending on
 * StoreContextService (which is empty without a tenant login).
 */
export interface KdsLaunchResponse {
  token: string;
  storeId: string;
  tenantId: string;
  storeName: string;
  posStoreCode: string;
  expiresAt: string;
  kitchens: KdsKitchenSummary[];
  /**
   * OMNI-2b.1 (F10) — the TENANT's channel-availability flag, resolved
   * server-side (a null column arrives as false). The board reads it to decide
   * whether the 86 control exists, so a KDS that learns its store from this
   * payload does not depend on the POS shell having loaded store settings in
   * the same browser.
   *
   * OPTIONAL because the payload is cached in localStorage: a board still
   * holding a payload minted before this release has no such field, and absent
   * means OFF — never "assume on".
   */
  channelAvailabilityEnabled?: boolean | null;
}

interface KdsLaunchEnvelope {
  success: boolean;
  data?: KdsLaunchResponse;
  error?: string;
}

/**
 * Frontend counterpart of the backend KdsTerminalLoginAsync flow. The POS
 * SPA route `/kds/s/{storeCode}` calls `launch(storeCode)` on init; the
 * service POSTs to the public endpoint, stores both the kdsToken and the
 * full launch payload in localStorage, and resolves with the payload so
 * the route resolver can hand it straight to the component.
 *
 * Trust model — Path A: storeCode IS the credential (matches kiosk +
 * pickup-display). All subsequent KDS API calls carry the kdsToken via
 * the auth interceptor's `isKdsApi` branch. Token TTL is 12h; on 401
 * the shell calls `launch()` again to mint a fresh one.
 *
 * The class is bound to `providedIn: 'root'` because there is exactly
 * one in-flight KDS session per tab and the cached launch payload is
 * a single tenant-scoped resource.
 */
@Injectable({ providedIn: 'root' })
export class KdsLaunchService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly baseUrl = environment.apiUrl;

  /**
   * Mint a kdsToken for the given storeCode. Persists both the token and
   * the full launch payload to localStorage before resolving.
   *
   * Returns `null` on any failure (network error, 404, 5xx) — the route
   * resolver should treat null as "redirect to a not-available
   * placeholder". The error reason is logged but not surfaced to the
   * caller because the BE deliberately returns uniform 404s for anti-
   * enumeration; we don't want to leak more than that.
   */
  launch(storeCode: string): Observable<KdsLaunchResponse | null> {
    if (!storeCode || storeCode.length > 32) {
      return of(null);
    }
    // POST (matches BE) — the launch produces a fresh JWT each call and
    // must never be cached.
    const url = `${this.baseUrl}/public/store/${encodeURIComponent(storeCode)}/kds-launch`;
    return this.http.post<KdsLaunchEnvelope>(url, {}).pipe(
      map(res => {
        if (!res.success || !res.data) {
          console.warn('[KdsLaunchService] launch failed:', res.error);
          return null;
        }
        return res.data;
      }),
      tap(data => {
        if (data) {
          // Persist token + payload. The interceptor reads KDS_TOKEN to
          // attach the bearer on KDS endpoints; the component reads
          // KDS_LAUNCH for storeId/storeName/kitchens.
          this.storage.setRaw(StorageKey.KDS_TOKEN, data.token);
          this.storage.set(StorageKey.KDS_LAUNCH, data);
        }
      }),
      catchError(err => {
        console.error('[KdsLaunchService] launch error:', err);
        return of(null);
      })
    );
  }

  /**
   * Read the cached launch payload from localStorage. Returns null if no
   * launch has happened in this tab yet (or storage was cleared).
   */
  getCachedLaunch(): KdsLaunchResponse | null {
    return this.storage.get<KdsLaunchResponse>(StorageKey.KDS_LAUNCH);
  }

  /**
   * Drop the cached token + payload. Called on 401 (token expired) or
   * when the operator explicitly exits KDS mode.
   */
  clear(): void {
    this.storage.remove(StorageKey.KDS_TOKEN);
    this.storage.remove(StorageKey.KDS_LAUNCH);
  }
}

/**
 * Angular route resolver for `/kds/s/{storeCode}`. Fires before navigation
 * completes, so the component never mounts without a valid kdsToken in
 * storage.
 *
 * Behaviour:
 *   • If a fresh launch succeeds → the payload is returned to the route
 *     (component reads `route.snapshot.data['kdsLaunch']`) and navigation
 *     proceeds.
 *   • If the launch fails (unknown storeCode / inactive store / network
 *     down) → the resolver redirects to `/kds-unavailable` (a plain
 *     placeholder card; matches the uniform-404 anti-enumeration posture
 *     of the BE).
 *
 * The resolver ALWAYS fires a fresh launch — it does NOT reuse a cached
 * payload — so the kdsToken's TTL is reset on each entry to the route
 * and the kitchens list is up-to-date.
 */
export const kdsLaunchResolver: ResolveFn<KdsLaunchResponse | null> = (route) => {
  const launcher = inject(KdsLaunchService);
  const router = inject(Router);

  const storeCode = route.paramMap.get('storeCode') ?? '';

  return launcher.launch(storeCode).pipe(
    tap(result => {
      if (!result) {
        // Bounce to a "this display is no longer available" landing.
        // Uses navigateByUrl rather than `createUrlTree` because resolver
        // return-value redirects in Angular 17+ are not first-class —
        // safer to drive the router imperatively here.
        void router.navigateByUrl('/kds-unavailable');
      }
    })
  );
};
